/**
 * The queue of things this device could not reach, as arithmetic.
 *
 * Everything here is pure and works on plain arrays, so the rules that decide
 * what gets kept — and, more importantly, what gets thrown away — can be tested
 * without a browser. The half that touches localStorage, `fetch` and the window
 * is errorReporter.ts, which is this file plus the side effects.
 *
 * The whole design exists because of one case: the server cannot record a
 * request that never arrived. So the device writes the note itself, keeps it
 * across reloads, and hands it over on the next request that does get through.
 */

/** Mirrors ClientErrorKind in server/src/client-errors.ts. */
export type ClientErrorKind =
  | 'api'
  | 'upload'
  | 'socket'
  | 'push'
  | 'crash'
  | 'lifecycle'
  | 'call';

/** Mirrors ClientErrorReport in server/src/client-errors.ts. */
export interface ClientErrorReport {
  /**
   * Minted here rather than on the server, which is what makes a retry free:
   * a batch whose reply was lost is sent again and lands on the rows it already
   * made (INSERT OR IGNORE on this id).
   */
  id: string;
  kind: ClientErrorKind;
  /** Which endpoint, socket or subscription step — the real one, ids and all. */
  detail: string;
  message: string;
  /** 0 when nothing answered at all; null when there was no request to answer. */
  status: number | null;
  offline: boolean;
  /** This device's clock. The server stamps its own arrival time beside it. */
  at: number;
}

/**
 * How many notes are kept while there is nowhere to send them. Two hundred
 * small objects is nothing to hold and about a day of a badly behaved network;
 * past that the oldest go, because an outage that long is described by its
 * first entries and its last, not by the four hundred in between.
 */
export const QUEUE_LIMIT = 200;

/**
 * How long one kind of failure stays silent after being recorded once.
 *
 * Without this a reconnect loop is a queue full of the same line. Thirty
 * seconds is long enough that a genuine outage costs a handful of rows and
 * short enough that "it broke again an hour later" is still two entries.
 */
export const THROTTLE_MS = 30_000;

/**
 * A path with its ids taken out, for grouping.
 *
 * `/chats/9f3.../messages` and `/chats/2b7.../messages` are the same failure
 * twice, and throttling them apart would let one broken network write a row per
 * conversation. The id stays in the report itself — this shortened form is only
 * ever used to decide whether a note is a repeat.
 */
export function normaliseRoute(path: string): string {
  const [withoutQuery = ''] = path.split('?');
  return withoutQuery
    .split('/')
    .map((segment) =>
      // Long and containing a digit is what every id in this app looks like and
      // what none of its route names do.
      segment.length >= 8 && /\d/.test(segment) ? ':id' : segment,
    )
    .join('/');
}

/** What counts as "the same failure" for the throttle. */
export function signatureOf(report: ClientErrorReport): string {
  return `${report.kind}|${normaliseRoute(report.detail)}|${report.status ?? '-'}`;
}

/**
 * Whether this failure is worth another row, remembering that it was.
 *
 * `seen` is kept outside the queue on purpose. Deriving the throttle from what
 * is queued would reset it every time a flush succeeded, which is exactly the
 * case a repeating error is loudest in: the queue would empty every few seconds
 * and refill with the same line.
 *
 * Entries older than the window are dropped as it grows, so a device that has
 * been open for a week does not accumulate a signature per conversation.
 */
export function admit(
  seen: Map<string, number>,
  signature: string,
  now: number,
  windowMs = THROTTLE_MS,
): boolean {
  const last = seen.get(signature);
  if (last !== undefined && now - last < windowMs) return false;

  if (seen.size > 100) {
    for (const [key, at] of seen) if (now - at >= windowMs) seen.delete(key);
  }
  seen.set(signature, now);
  return true;
}

/**
 * Failures that are the development server rather than the app.
 *
 * `dev-sw.js` is vite-plugin-pwa's stand-in service worker, and it fails to
 * register on essentially every dev page load — which arrives here as an
 * unhandled rejection and writes a row each time. Nothing in the built app has
 * that file, so nothing is being hidden in production; what it costs to leave
 * in is a local `crash` count made entirely of one artifact, which is worse
 * than useless while testing this very feature.
 *
 * Kept as a list rather than a blanket "report nothing in dev", because dev is
 * where the reporter itself gets exercised.
 */
const DEV_NOISE = [/dev-sw\.js/];

/** Whether this is worth a row at all. */
export function isNoise(message: string): boolean {
  return DEV_NOISE.some((pattern) => pattern.test(message));
}

/** Appends, dropping the oldest once the queue is full. */
export function enqueue(
  queue: ClientErrorReport[],
  report: ClientErrorReport,
  limit = QUEUE_LIMIT,
): ClientErrorReport[] {
  const next = [...queue, report];
  return next.length <= limit ? next : next.slice(next.length - limit);
}

/** What is left after a batch was accepted. Matched by id, not by position. */
export function withoutIds(queue: ClientErrorReport[], ids: string[]): ClientErrorReport[] {
  const sent = new Set(ids);
  return queue.filter((report) => !sent.has(report.id));
}

/**
 * Reads the stored queue back, tolerating anything.
 *
 * This runs at startup on data that survived a reload, a version of the app
 * that may have written a different shape, and whatever else shares the key. A
 * queue of diagnostics is the last thing in the app that may throw on the way
 * in, so anything unrecognisable is simply not there.
 */
export function readQueue(raw: string | null): ClientErrorReport[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isReport).slice(-QUEUE_LIMIT);
  } catch {
    return [];
  }
}

function isReport(value: unknown): value is ClientErrorReport {
  if (typeof value !== 'object' || value === null) return false;
  const report = value as Record<string, unknown>;
  return (
    typeof report.id === 'string' &&
    typeof report.kind === 'string' &&
    typeof report.message === 'string' &&
    typeof report.at === 'number'
  );
}
